import type { Rarity } from '#src/__generated__/globalTypes.ts';
import { useConfigContext } from '#src/contexts/config/index.ts';
import { getRarityColor } from '#src/lib/colors.ts';
import type { Color } from '#src/style/types.ts';

import { SET_BASE } from './base/index.ts';
import { SET_COLORS } from './colors/index.ts';
import { SET_STELLAR } from './stellar/index.ts';
import { SET_WINTER } from './wntr/index.ts';

const SETS = [SET_WINTER, SET_STELLAR, SET_COLORS];

export const useGetCardSetConfig = () => {
  const { cardSet } = useConfigContext();

  return (slug?: string | null) => {
    // currentSet can be undefined during transition, so we fallback to nextSet
    // to start showing the next set branding
    const setSlug = slug || cardSet?.currentSet?.slug || cardSet?.nextSet?.slug;

    return SETS.find(s => s.slug === setSlug) ?? SET_BASE;
  };
};

export const useCommonGameSet = () => {
  const { cardSet } = useConfigContext();
  const setToDisplay = cardSet?.currentSet || cardSet?.nextSet;
  const getCardSetConfig = useGetCardSetConfig();

  return {
    ...getCardSetConfig(),
    endDate: setToDisplay?.endDate,
    name: setToDisplay?.name,
  };
};

export const useCurrentSetBackground = () => {
  const getCardSetConfig = useGetCardSetConfig();
  const currentSet = getCardSetConfig();
  return currentSet.background;
};

export function useGetSetBackground() {
  const getCardSetConfig = useGetCardSetConfig();
  return (setSlug?: string) => {
    const setConfig = getCardSetConfig(setSlug);
    return setConfig.background;
  };
}

export const useGetSetColor = () => {
  const getCardSetConfig = useGetCardSetConfig();
  return (setSlug: Nullable<string>) => {
    const setConfig = getCardSetConfig(setSlug);
    return setConfig.colors.main;
  };
};

export const useIsSpecialCardSet = () => {
  return (setSlug: Nullable<string>) => {
    return !!setSlug && setSlug !== 'common-base-set';
  };
};

export const useIsCurrentCardSet = () => {
  const commonGameSet = useCommonGameSet();
  return (setSlug: Nullable<string>) => {
    return !!setSlug && setSlug === commonGameSet.slug;
  };
};

const MIX_SHARD_COLOR: Color = 'var(--c-green-400)';

export const useGetRarityColor = () => {
  const getSetColor = useGetSetColor();
  return (
    rarity: Rarity,
    opts?: { opacity?: number; cardSet?: Nullable<string> }
  ): Color => {
    const { opacity = 1, cardSet } = opts || {};
    if (cardSet) {
      return getSetColor(cardSet);
    }
    return getRarityColor(rarity, { opacity });
  };
};

export const useGetRarityShardColor = () => {
  const getRarityColorWithCardSet = useGetRarityColor();
  return (rarity: Nullable<Rarity>, cardSet: Nullable<string>) => {
    if (rarity) {
      return getRarityColorWithCardSet(rarity, { cardSet });
    }
    return MIX_SHARD_COLOR;
  };
};

export const useSetLogoImage = () => {
  const getCardSetConfig = useGetCardSetConfig();
  return (slug: string | undefined) => {
    const setConfig = getCardSetConfig(slug);
    return setConfig.visual.brand.logoImage;
  };
};
